import React, { FC, useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  TwButton,
  TwButtonType,
  TwLoadingButton
} from '../../../../components/TwButtons/TwButtons';
import {
  useGetFuelCardConfigsQuery,
  useGetFuelSyncStatusQuery,
  useSyncFuelTransactionsMutation
} from '../../../../redux/api/fuelcard/fuelcardApi';
import TwDataGrid, { elementRenderBasic } from '../../../../components/TwDataGrid/TwDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  FuelCardConfigResponse,
  FuelCardConfigTableData,
  FuelCardProvider,
  FuelCardSyncStatusResponse
} from '../../../../redux/api/fuelcard/fuelcard.model';
import ConnectModal from './ConnectModal';
import DisconnectModal from './DisconnectModal';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import DateUtils from '../../../../utils/dateUtils';

interface FuelcardAccountManagerSettingProps {
  divisionId: string;
}

const columns: GridColDef[] = [
  {
    field: 'username',
    headerName: 'Username',
    renderCell: elementRenderBasic,
    maxWidth: 150,
    sortable: false
  },
  {
    field: 'fuelcardProvider',
    headerName: 'Type',
    renderCell: elementRenderBasic,
    maxWidth: 150,
    sortable: false
  },
  {
    field: 'lastSync',
    headerName: 'Last Sync',
    renderCell: elementRenderBasic,
    maxWidth: 150,
    sortable: false
  },
  {
    field: 'actions',
    headerName: '',
    sortable: false,
    width: 100,
    renderCell: elementRenderBasic
  }
];

export const FuelcardAccountManagerSetting: FC<FuelcardAccountManagerSettingProps> = ({
  divisionId
}) => {
  const { addNotification } = useNotifications();

  const [isEFSLoginOpen, setIsEFSLoginOpen] = useState<boolean>(false);

  const { data: fuelcardConfigs, isLoading, error } = useGetFuelCardConfigsQuery(divisionId);
  const [syncFuelTransactions, { isLoading: isSyncing }] = useSyncFuelTransactionsMutation();
  const { data: fuelSyncStatuses, isLoading: isSyncStatusLoading } =
    useGetFuelSyncStatusQuery(divisionId);

  const [tableData, setTableData] = useState<FuelCardConfigTableData[]>([]);

  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const transformFuelCardConfigs = (
    configs: FuelCardConfigResponse[],
    syncStatuses: FuelCardSyncStatusResponse[]
  ): void => {
    const transformedConfigs: FuelCardConfigTableData[] = configs.map((config) => {
      const syncStatus = syncStatuses.find(
        (status) =>
          status.username === config.username && status.provider === config.fuelcardProvider
      );

      const lastSyncDate = syncStatus?.lastSuccess ? DateUtils.getFullDateAtTime(new Date(syncStatus.lastSuccess)) : '-';
      return {
        id: `${config.divisionId}-${config.fuelcardProvider}-${config.username}`,
        username: config.username,
        fuelcardProvider: config.fuelcardProvider,
        lastSync: lastSyncDate,
        actions: (
          <TwButton
            key="2"
            type={TwButtonType.text}
            label="Disconnect"
            disabled={false}
            action={(): void => {
              setSelectedUsername(config.username);
              setIsDeleteModalOpen(true);
            }}
          />
        )
      };
    });

    setTableData(transformedConfigs);
  };

  useEffect(() => {
    if (error) {
      setTableData([]);
    } else {
      transformFuelCardConfigs(fuelcardConfigs || [], fuelSyncStatuses || []);
    }
  }, [fuelcardConfigs, error, fuelSyncStatuses]);

  const handleSyncFuelTransactions = (): void => {
    syncFuelTransactions(divisionId)
      .unwrap()
      .then(() => {
        addNotification({
          message: 'Synchronization has been requested',
          variant: 'success',
          timeToShow: 3000
        });
      })
      .catch(() => {
        addNotification({
          message: 'Failed to request synchronization. Please try again later.',
          variant: 'error',
          timeToShow: 3000
        });
      });
  };

  return (
    <>
      <Stack className="invoicing__fuelcard-manager-container">
        <Typography variant="h5" className="invoicing__subtitle">
          Wex | EFS | FleetOne
        </Typography>
        <Typography className="settings__subtitle">
          Connect to Wex | EFS | FleetOne to import fuel card transactions to Truckbase
        </Typography>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          marginBottom="15px"
          marginTop="10px"
        >
          <TwButton
            type={TwButtonType.secondary}
            label="Connect"
            action={(): void => {
              setIsEFSLoginOpen(true);
            }}
          />

          <TwLoadingButton
            type={TwButtonType.primary}
            label="Sync now"
            action={handleSyncFuelTransactions}
            loading={isSyncing}
            fullwidth={false}
          />
        </Stack>

        <TwDataGrid
          columns={columns}
          rows={tableData}
          loading={isLoading}
          hideFooter
          disableRowSelectionOnClick
        />
      </Stack>

      <ConnectModal
        open={isEFSLoginOpen}
        onClose={(): void => setIsEFSLoginOpen(false)}
        provider={FuelCardProvider.EFS}
        divisionId={divisionId}
      />

      <DisconnectModal
        open={isDeleteModalOpen}
        onClickClose={(): void => setIsDeleteModalOpen(false)}
        username={selectedUsername}
        provider={FuelCardProvider.EFS}
        divisionId={divisionId}
      />
    </>
  );
};

export default FuelcardAccountManagerSetting;
